/**
 * usePageTracking - Hook to track page views with analytics
 *
 * Tracks page views when route changes and user has given consent.
 * Also tracks engagement time for benchmarking metrics.
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView, trackEngagement, trackScrollDepth } from "@/utils/analytics";
import { trackPixelPageView } from "@/utils/marketingPixels";
import { hasAnalyticsConsent, hasMarketingConsent } from "@/utils/consent";

/**
 * Hook to track page views on route changes
 * Enhanced with engagement tracking for benchmarking
 */
export function usePageTracking() {
  const location = useLocation();
  const engagementStartTime = useRef<number>(0);
  const lastScrollDepth = useRef<number>(0);

  useEffect(() => {
    // Reset engagement tracking for new page
    engagementStartTime.current = Date.now();
    engagementStartTime.current = Date.now();
    lastScrollDepth.current = 0;

    // Track page view with analytics if consent given
    if (hasAnalyticsConsent()) {
      trackPageView(location.pathname, document.title);
    }

    // Track page view with marketing pixels if consent given
    if (hasMarketingConsent()) {
      trackPixelPageView(location.pathname, document.title);
    }

    // Only continue with engagement tracking if analytics consent is given
    if (!hasAnalyticsConsent()) {
      return;
    }

    // Track engagement time when user leaves page or after 10 seconds
    const trackEngagementOnUnload = () => {
      const engagementTime = Date.now() - engagementStartTime.current;
      if (engagementTime > 1000) {
        // Only track if user was engaged for at least 1 second
        trackEngagement(engagementTime);
      }
    };

    // Track engagement periodically (every 10 seconds)
    const engagementInterval = setInterval(() => {
      const engagementTime = Date.now() - engagementStartTime.current;
      trackEngagement(engagementTime);
    }, 10000);

    // Track scroll depth for engagement benchmarking
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;

      // Track at 25%, 50%, 75%, and 100% milestones
      if (
        (scrollPercent >= 25 && lastScrollDepth.current < 25) ||
        (scrollPercent >= 50 && lastScrollDepth.current < 50) ||
        (scrollPercent >= 75 && lastScrollDepth.current < 75) ||
        (scrollPercent >= 100 && lastScrollDepth.current < 100)
      ) {
        trackScrollDepth(scrollPercent);
        lastScrollDepth.current = scrollPercent;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("beforeunload", trackEngagementOnUnload);
    window.addEventListener("pagehide", trackEngagementOnUnload);

    return () => {
      clearInterval(engagementInterval);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("beforeunload", trackEngagementOnUnload);
      window.removeEventListener("pagehide", trackEngagementOnUnload);
      // Track final engagement time
      trackEngagementOnUnload();
    };
  }, [location.pathname]);
}
